import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, User, X, ArrowRight } from "lucide-react";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { PageHero, BtnPrimary, BtnGhost, CtaBand, Crumbs } from "../components/Shared";
import { SCHEDULE, IMG, CHALKIT_MEMBER_URL, SITE, COACHES } from "../data/mock";

const coachSlug = (name) => {
  const c = COACHES.find(
    (k) => k.name === name || k.name.split(" ")[0] === name || k.name.toLowerCase().startsWith(name.toLowerCase())
  );
  return c ? c.slug : null;
};

const classLink = (name) => {
  if (name.startsWith("CrossFit")) return "/classes/crossfit";
  if (name.includes("HYROX-Hybrid")) return "/classes/hyrox-hybrid-core-zone-2";
  if (name.includes("HYROX")) return "/hyrox";
  if (name.includes("Powerlifting")) return "/classes/powerlifting";
  if (name.includes("Olympic")) return "/classes/olympic-weightlifting";
  if (name.includes("Engine")) return "/classes/big-engine";
  if (name.includes("Open Gym")) return "/classes/open-gym";
  return null;
};

const classColor = (name) => {
  if (name.includes("HYROX")) return "border-[#9D0423]/50 text-[#E56A79]";
  if (name.includes("Olympic")) return "border-amber-500/40 text-amber-300";
  if (name.includes("Open Gym")) return "border-sky-500/40 text-sky-300";
  return "border-white/15 text-neutral-200";
};

const to24 = (t) => {
  const [time, mer] = t.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (mer === "PM" && h !== 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
};

const eventSeriesSchemas = () => {
  const map = {};
  SCHEDULE.days.forEach((d) =>
    d.slots.forEach((s) => {
      map[s.name] = map[s.name] || {};
      const t = to24(s.time);
      map[s.name][t] = map[s.name][t] || new Set();
      map[s.name][t].add(`https://schema.org/${d.day}`);
    })
  );
  return Object.entries(map).map(([name, times]) => ({
    "@context": "https://schema.org",
    "@type": "EventSeries",
    name: `${name} — ${SITE.name}`,
    location: { "@id": `${SITE.url}/#business` },
    organizer: { "@id": `${SITE.url}/#organization` },
    eventSchedule: Object.entries(times).map(([t, days]) => ({
      "@type": "Schedule",
      byDay: [...days],
      startTime: t,
      repeatFrequency: "P1W",
      scheduleTimezone: "America/New_York",
    })),
  }));
};

const slotId = (day, time) => `${day.toLowerCase()}-${time.replace(/[:\s]/g, "").toLowerCase()}`;

function CoachNames({ coach }) {
  const parts = coach.split(" & ");
  return (
    <>
      {parts.map((p, i) => {
        const slug = coachSlug(p);
        return (
          <React.Fragment key={p}>
            {i > 0 && <span className="text-neutral-600"> & </span>}
            {slug ? (
              <Link
                to={`/coaches/${slug}`}
                className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-400 hover:text-[#E56A79] underline decoration-white/20 underline-offset-4 hover:decoration-[#E56A79]/60 transition-colors"
                data-testid={`schedule-coach-link-${slug}`}
              >
                {p}
              </Link>
            ) : (
              <span className="font-mono text-[11px] uppercase tracking-[0.16em]">{p}</span>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

export default function Schedule() {
  const today = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const defaultIdx = Math.max(0, SCHEDULE.days.findIndex((d) => d.day === today));
  const [active, setActive] = useState(defaultIdx);
  const [booking, setBooking] = useState(null);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Schedule", path: "/schedule" },
  ];

  return (
    <>
      <Seo
        title="Class Schedule | West Roxbury Gym — Boston Iron Grit"
        description="Weekly class schedule at Boston Iron Grit in West Roxbury: CrossFit from 5:15 AM, HYROX, powerlifting, Olympic lifting, B.I.G. Engine and open gym — book any class online."
        path="/schedule"
        schemas={[breadcrumbSchema(crumbs), ...eventSeriesSchemas()]}
      />
      <PageHero
        label="Weekly Schedule"
        title={<>Fire Breathers. Noon crew.<br /><span className="text-neutral-500">Night shift.</span></>}
        sub="The Boston Iron Grit weekly class schedule in West Roxbury — CrossFit, HYROX, powerlifting, Olympic weightlifting, B.I.G. Engine and open gym. Up to eight classes a day on weekdays from 5:15 AM, plus weekend sessions. Tap any class to book."
        image={IMG.rower}
      />

      <section className="bg-[#0A0A0B] pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
        </div>
      </section>

      <section className="bg-[#0A0A0B] py-10 lg:py-14">
        <div className="max-w-7xl mx-auto px-6">
          {/* Day tabs (visual filter — all seven days stay in the document for crawlers) */}
          <div className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Day of week">
            {SCHEDULE.days.map((d, i) => (
              <button
                key={d.day}
                role="tab"
                aria-selected={i === active}
                aria-controls={`day-panel-${d.day.toLowerCase()}`}
                data-testid={`schedule-tab-${d.day.toLowerCase()}`}
                onClick={() => setActive(i)}
                className={`px-5 py-3 font-mono text-[12px] uppercase tracking-[0.14em] border transition-all duration-200 ${
                  i === active
                    ? "bg-[#9D0423] border-[#9D0423] text-white"
                    : "bg-transparent border-white/10 text-neutral-400 hover:border-white/30 hover:text-white"
                }`}
              >
                {d.day.slice(0, 3)}
                {d.day === today && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-current align-middle" />}
              </button>
            ))}
          </div>

          {/* All seven days rendered in the DOM; non-active days are visually hidden */}
          {SCHEDULE.days.map((d, i) => (
            <div
              key={d.day}
              id={`day-panel-${d.day.toLowerCase()}`}
              role="tabpanel"
              className={i === active ? "" : "hidden"}
              data-testid={`schedule-day-${d.day.toLowerCase()}`}
            >
              <div className="flex items-baseline gap-4 mb-6">
                <h2 className="font-heading font-extrabold uppercase text-3xl text-white tracking-tight">{d.day}</h2>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">{d.slots.length} sessions</span>
              </div>
              <div className="grid gap-px bg-white/5 border border-white/5">
                {d.slots.map((s) => (
                  <div key={`${s.time}-${s.name}`} className="grid grid-cols-[88px_1fr_auto] sm:grid-cols-[130px_1fr_auto_auto] items-center bg-[#0D0D0F] hover:bg-[#121214] transition-colors px-4 sm:px-7 py-5 gap-3 sm:gap-5">
                    <div className="flex items-center gap-2.5">
                      <Clock size={14} className="text-[#9D0423] shrink-0" />
                      <span className="font-mono text-[13px] text-white tracking-wide">{s.time}</span>
                    </div>
                    <div>
                      {classLink(s.name) ? (
                        <Link
                          to={classLink(s.name)}
                          data-testid={`schedule-class-link-${slotId(d.day, s.time)}`}
                          className={`inline-block font-heading font-bold uppercase text-base tracking-tight border-l-2 pl-3 hover:text-white transition-colors ${classColor(s.name)}`}
                        >
                          {s.name}
                        </Link>
                      ) : (
                        <span className={`inline-block font-heading font-bold uppercase text-base tracking-tight border-l-2 pl-3 ${classColor(s.name)}`}>
                          {s.name}
                        </span>
                      )}
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-neutral-500 justify-end">
                      <User size={13} />
                      <CoachNames coach={s.coach} />
                    </div>
                    <button
                      onClick={() => setBooking({ day: d.day, ...s })}
                      data-testid={`schedule-book-${slotId(d.day, s.time)}`}
                      className="justify-self-end font-mono font-semibold text-[11px] uppercase tracking-[0.16em] text-white border border-[#9D0423]/60 hover:bg-[#9D0423] px-4 py-2.5 transition-colors"
                    >
                      Book
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-8 p-6 bg-[#121214] border border-white/5 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <p className="text-sm text-neutral-300">{SCHEDULE.openGym}</p>
              <p className="mt-1.5 text-[13px] text-neutral-500">{SCHEDULE.note}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <BtnPrimary href={CHALKIT_MEMBER_URL}>Reserve on ChalkIt Pro</BtnPrimary>
              <BtnGhost to="/drop-in">Visiting? Drop In</BtnGhost>
            </div>
          </div>
        </div>
      </section>

      {/* Booking dialog */}
      {booking && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" data-testid="booking-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setBooking(null)} data-testid="booking-modal-overlay" />
          <div className="relative w-full sm:max-w-md bg-[#121214] border border-white/10 p-7 sm:p-8">
            <button
              onClick={() => setBooking(null)}
              aria-label="Close booking dialog"
              data-testid="booking-modal-close"
              className="absolute top-4 right-4 p-2 text-neutral-500 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79]">Book this class</p>
            <h3 className="mt-2 font-heading font-extrabold uppercase text-2xl text-white tracking-tight" data-testid="booking-modal-title">{booking.name}</h3>
            <p className="mt-1.5 font-mono text-[12px] uppercase tracking-[0.16em] text-neutral-400">
              {booking.day} · {booking.time}
              {booking.coach !== "Staffed" ? ` · Coach ${booking.coach}` : ""}
            </p>
            <div className="mt-7 space-y-3">
              <a
                href={CHALKIT_MEMBER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-member-chalkit"
                className="flex items-center justify-between gap-3 bg-[#9D0423] hover:bg-[#7E031C] text-white px-5 py-4 transition-colors"
              >
                <span className="text-left">
                  <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.14em]">I'm a member — Reserve on ChalkIt Pro</span>
                  <span className="block mt-1 text-[11px] text-white/70">Log in to the ChalkIt Pro app to reserve your spot</span>
                </span>
                <ArrowRight size={15} className="shrink-0" />
              </a>
              <Link
                to="/get-started"
                data-testid="booking-new-goal-review"
                className="flex items-center justify-between gap-3 border border-white/15 hover:border-white/50 text-white px-5 py-4 transition-colors"
              >
                <span className="text-left">
                  <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.14em]">I'm new — Book a Free Goal Review</span>
                  <span className="block mt-1 text-[11px] text-neutral-500">20 minutes with a coach — no workout, no pitch</span>
                </span>
                <ArrowRight size={15} className="shrink-0" />
              </Link>
              <Link
                to="/drop-in"
                data-testid="booking-dropin"
                className="flex items-center justify-between gap-3 border border-white/15 hover:border-white/50 text-white px-5 py-4 transition-colors"
              >
                <span className="text-left">
                  <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.14em]">Visiting athlete — Drop In ($35)</span>
                  <span className="block mt-1 text-[11px] text-neutral-500">Experienced CrossFit athletes, book ahead</span>
                </span>
                <ArrowRight size={15} className="shrink-0" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <CtaBand />
    </>
  );
}
